import { useEffect, useRef } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';

import { getCurrentTranscriptOffset, useIncidentCall } from '@ngsos/portal-core/portal';
import type { IncidentCall } from '@ngsos/portal-core/portal';

import {
  DataPanel,
  DataPanelInlineItem,
  DataPanelRow,
  EmptyDataMessage,
  LoadingFailedBox,
  NgBreadcrumbItem,
  NgBreadcrumbs,
  NgPageHeader,
} from '@/ui';
import { manageScrolling } from '@/lib/chat/chatScrolling';
import { toCalendarTime } from '@/lib/time';
import { incidentDetailRoute } from '@/features/shared/routes';
import { CallTranscriptEventTimeline } from './CallTranscriptEventTimeline';
import { CallTranscriptMessageList } from './CallTranscriptMessageList';

const TRANSCRIPT_SCROLL_CONTAINER_CLASS = 'incident-call-transcript-page__panel-content--transcript';

function getStatusLabel(item: IncidentCall, t: (key: string) => string): string {
  return item.endTimeUtc !== null ? t('CallTranscript_Status_Ended') : t('CallTranscript_Status_Active');
}

export function IncidentCallTranscriptPage() {
  const { t, i18n } = useTranslation();
  const { incidentId = '' } = useParams<'incidentId'>();
  const language = i18n.language || 'en';

  const { call: callNotification, isLoading, error } = useIncidentCall(incidentId);

  const errorText = error == null ? '' : error instanceof Error ? error.message : String(error);
  const showError = errorText.trim() !== '';

  const currentTranscript = callNotification?.callTranscript ?? null;
  const timelineItems = currentTranscript?.detectedEvents ?? [];
  const currentTranscriptOffset = currentTranscript === null ? null : getCurrentTranscriptOffset(currentTranscript);
  const hasTranscriptMessages = (currentTranscript?.messages.length ?? 0) > 0;

  const scrollCleanupRef = useRef<(() => void) | undefined>(undefined);
  useEffect(() => {
    if (hasTranscriptMessages) {
      const cleanup = manageScrolling(TRANSCRIPT_SCROLL_CONTAINER_CLASS);
      if (cleanup !== undefined) {
        scrollCleanupRef.current = cleanup;
      }
    }
  });
  useEffect(
    () => () => {
      scrollCleanupRef.current?.();
    },
    [],
  );

  const pageTitle = t('PageTitle_CallTranscript');
  const getEndTimeLabel = (item: IncidentCall): string =>
    item.endTimeUtc !== null ? toCalendarTime(item.endTimeUtc, language) : t('CallTranscript_Status_InProgress');

  return (
    <div className="p-3">
      <NgBreadcrumbs>
        <NgBreadcrumbItem title={t('PageTitle_Incident')} to={incidentDetailRoute(incidentId)} />
      </NgBreadcrumbs>

      <NgPageHeader title={pageTitle} />
      <h1>{pageTitle}</h1>

      {isLoading ? (
        <div className="incident-call-transcript-page__status">{t('CallTranscript_Loading')}</div>
      ) : showError ? (
        <>
          <LoadingFailedBox />
          <span>{errorText}</span>
        </>
      ) : callNotification === null || currentTranscript === null || currentTranscript.messages.length === 0 ? (
        <EmptyDataMessage text={t('CallTranscript_Empty')} />
      ) : (
        <div className="incident-call-transcript-page">
          <div className="incident-call-transcript-page__content">
            <div className="incident-call-transcript-page__main">
              <section className="incident-call-transcript-page__panel incident-call-transcript-page__panel--summary">
                <div className="incident-call-transcript-page__panel-header">
                  <div className="incident-call-transcript-page__eyebrow">{t('CallTranscript_SummaryHeader')}</div>
                </div>
                <div className="incident-call-transcript-page__summary">
                  {currentTranscript.summary == null || currentTranscript.summary.trim() === ''
                    ? t('CallTranscript_SummaryEmpty')
                    : currentTranscript.summary}
                </div>
              </section>

              <section className="incident-call-transcript-page__panel incident-call-transcript-page__panel--transcript">
                <div className="incident-call-transcript-page__panel-header">
                  <div className="incident-call-transcript-page__eyebrow">{t('CallTranscript_TranscriptHeader')}</div>
                  <div className="incident-call-transcript-page__timeline-note">
                    {t('CallTranscript_TranscriptSubtitle')}
                  </div>
                </div>
                <div className="incident-call-transcript-page__panel-content incident-call-transcript-page__panel-content--transcript">
                  <CallTranscriptMessageList transcript={currentTranscript} />
                </div>
              </section>
            </div>

            <aside className="incident-call-transcript-page__sidebar">
              <section className="incident-call-transcript-page__panel incident-call-transcript-page__panel--info">
                <div className="incident-call-transcript-page__panel-header">
                  <div className="incident-call-transcript-page__eyebrow">{t('CallTranscript_CallInfoHeader')}</div>
                </div>
                <DataPanel>
                  <DataPanelRow>
                    <DataPanelInlineItem title={t('CallTranscript_CallInfo_CallId')} value={callNotification.callId} />
                    <DataPanelInlineItem
                      title={t('CallTranscript_CallInfo_Status')}
                      value={getStatusLabel(callNotification, t)}
                    />
                  </DataPanelRow>
                  <DataPanelRow>
                    <DataPanelInlineItem
                      title={t('CallTranscript_CallInfo_Started')}
                      value={toCalendarTime(callNotification.startTimeUtc, language)}
                    />
                    <DataPanelInlineItem
                      title={t('CallTranscript_CallInfo_Ended')}
                      value={getEndTimeLabel(callNotification)}
                    />
                  </DataPanelRow>
                </DataPanel>
              </section>

              <section className="incident-call-transcript-page__panel incident-call-transcript-page__panel--timeline">
                <div className="incident-call-transcript-page__panel-header">
                  <div className="incident-call-transcript-page__eyebrow">{t('CallTranscript_TimelineHeader')}</div>
                  <div className="incident-call-transcript-page__timeline-note">
                    {t('CallTranscript_TimelineSubtitle')}
                  </div>
                </div>
                <div className="incident-call-transcript-page__panel-content incident-call-transcript-page__panel-content--timeline">
                  <CallTranscriptEventTimeline events={timelineItems} currentOffset={currentTranscriptOffset} />
                </div>
              </section>
            </aside>
          </div>
        </div>
      )}
    </div>
  );
}
